import { useEffect, useState, Fragment } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Truck,
  Store,
  X,
  ShieldCheck,
  MapPin,
  Clock,
  CreditCard,
  Headphones,
  Package,
  Star,
  Zap,
  Gift,
  Heart,
} from "lucide-react";
import bannerBg from "@/assets/banner-electronics.jpg";
import { ENDPOINTS } from "../../config";

interface TopBarItem {
  text: string;
  icon?: string;
}

const ICON_MAP: Record<string, LucideIcon> = {
  "shield-check": ShieldCheck,
  "map-pin": MapPin,
  truck: Truck,
  clock: Clock,
  "credit-card": CreditCard,
  headphones: Headphones,
  package: Package,
  store: Store,
  star: Star,
  zap: Zap,
  gift: Gift,
  heart: Heart,
};

const DEFAULT_ITEMS: TopBarItem[] = [
  { text: "Free 1-Day Delivery in Nairobi", icon: "truck" },
  { text: "30 Stores Nationwide", icon: "store" },
];

export function TopBar() {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState<TopBarItem[]>(DEFAULT_ITEMS);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(ENDPOINTS.HOME_KPIS.PUBLIC);
        const data = await res.json();
        if (data?.isOk && Array.isArray(data.topBar) && data.topBar.length) {
          setItems(data.topBar);
        }
      } catch (error) {
        console.error("Failed to load top bar items:", error);
      }
    };
    load();
  }, []);

  if (!open || !items.length) return null;

  return (
    <div className="relative overflow-hidden text-primary-foreground text-xs sm:text-sm">
      {/* Background image */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bannerBg})` }}
      />
      {/* Gradient overlay for legibility */}
      <div aria-hidden className="absolute inset-0 bg-gradient-brand opacity-85" />
      <div className="relative container mx-auto flex items-center justify-center gap-4 sm:gap-6 px-4 py-2 font-medium">
        {items.map((item, index) => {
          const Icon = ICON_MAP[item.icon || ""] || Truck;
          return (
            <Fragment key={`${item.text}-${index}`}>
              {index > 0 && (
                <span className="hidden sm:inline opacity-60">•</span>
              )}
              <span
                className={`items-center gap-1.5 ${index === 0 ? "flex" : "hidden sm:flex"}`}
              >
                <Icon className="h-3.5 w-3.5" /> {item.text}
              </span>
            </Fragment>
          );
        })}
      </div>
      <button
        onClick={() => setOpen(false)}
        aria-label="Dismiss banner"
        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 rounded-full p-1 hover:bg-black/20 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
